import {
  CreditNoteReasons,
  PaymentStatuses,
  ResaleFragment,
  ResaleStatusReasons,
  ResaleStatuses,
} from "@generated/graphql";

import { toFriendlyText } from "@util/index";

interface MapStatusToDisplayFriendlyTextProps {
  status?: PaymentStatuses | ResaleStatuses | CreditNoteReasons;
  isExternalUser?: boolean;
  showInvoiceRequestSent?: boolean;
}

interface LabelAndClassName {
  label: string;
  className: string | string[];
}

export type ResaleGroup = {
  batch_grouping_id: string;
  resales: ResaleFragment[];
  isProvisionalEligibleExist?: boolean;
};

export const calculateTotalRoyaltyForResales = (resales: ResaleFragment[]) => {
  return resales.reduce((sum, { royalty }) => sum + (royalty || 0), 0);
};

export const mapResaleStatusReasonsToDisplayFriendlyText = (resaleStatusReason: ResaleStatusReasons): string => {
  const map: { [key in ResaleStatusReasons]: string } = {
    [ResaleStatusReasons.LIVING_ARTIST_NOT_YET_LOCATED]: "Living artist, not yet located",
    [ResaleStatusReasons.LIVING_ARTIST_INDIGENOUS_NOT_MEMBER_OF_ART_CENTRE]:
      "Living artist, indigenous, not member of Art Centre",
    [ResaleStatusReasons.DECEASED_ARTIST]: "Deceased artist",
    [ResaleStatusReasons.BANK_DETAILS_REQUIRED]: "Bank details required",
    [ResaleStatusReasons.DECEASED_INDIGENOUS_ARTIST]: "Deceased indigenous artist",
    [ResaleStatusReasons.OVERSEAS_ARTIST]: "Overseas artist",
    [ResaleStatusReasons.OTHER]: "Other",
    [ResaleStatusReasons.CHARITY_EVENT]: "Charity event",
    [ResaleStatusReasons.COLLECT_THEMSELVES]: "Collect themselves",
    [ResaleStatusReasons.ARRANGEMENT_WITH_AGENT]: "Arrangement with agent",
    [ResaleStatusReasons.RIGHTSHOLDER_SALE]: "Rightsholder sale",
    [ResaleStatusReasons.NEGATIVE_IMPACT_ON_ARTISTS_SALES]: "Negative impact on artist’s sales",
    [ResaleStatusReasons.AUTO_UPDATE]: "Auto update",
    [ResaleStatusReasons.UNKNOWN]: "Unknown",
  };
  return map[resaleStatusReason];
};

const mapResaleStatusToClass = (resaleStatus: ResaleStatuses): string => {
  const map: { [key in ResaleStatuses]: string } = {
    [ResaleStatuses.CONFIRMED_ELIGIBLE]: "confirmed-eligible",
    [ResaleStatuses.CONFIRMED_INELIGIBLE]: "confirm-ineligible",
    [ResaleStatuses.INVALID]: "invalid",
    [ResaleStatuses.OPTED_OUT]: "opted-out",
    [ResaleStatuses.PROVISIONALLY_ELIGIBLE]: "provisionally-eligible",
    [ResaleStatuses.PROVISIONALLY_INELIGIBLE]: "provisionally-ineligible",
    [ResaleStatuses.RESEARCH_REQUIRED]: "research-required",
    [ResaleStatuses.RETURNED]: "returned",
    [ResaleStatuses.CLOSED]: "closed",
  };
  return map[resaleStatus] || "default-class";
};

export const mapStatusToDisplayFriendlyText = ({
  status,
  isExternalUser = false,
  showInvoiceRequestSent = false,
}: MapStatusToDisplayFriendlyTextProps): LabelAndClassName | undefined => {
  if (!status) {
    return undefined;
  }

  if (Object.values(ResaleStatuses).includes(status as ResaleStatuses)) {
    const defaultClassName = mapResaleStatusToClass(status as ResaleStatuses);
    const className = isExternalUser ? ["external-user", defaultClassName] : defaultClassName;

    if (isExternalUser) {
      switch (status) {
        case ResaleStatuses.PROVISIONALLY_ELIGIBLE:
          return { label: "Under review", className };
        case ResaleStatuses.PROVISIONALLY_INELIGIBLE:
          return { label: "Ineligible", className };
        case ResaleStatuses.CONFIRMED_ELIGIBLE:
          return { label: "Confirmed Eligible", className };
        case ResaleStatuses.RESEARCH_REQUIRED:
          return { label: "Research Required", className };
        case ResaleStatuses.OPTED_OUT:
          return { label: "Opted out", className };
        case ResaleStatuses.CONFIRMED_INELIGIBLE:
          return { label: "Confirmed ineligible", className };
        case ResaleStatuses.RETURNED:
          return { label: "Returned", className };
        case ResaleStatuses.CLOSED:
          return { label: "Closed", className };
        default:
          return { label: "Undefined", className: "undefined-badge" };
      }
    }

    return {
      label: toFriendlyText(status as string),
      className,
    };
  }

  if (Object.values(PaymentStatuses).includes(status as PaymentStatuses)) {
    const map: { [key in PaymentStatuses]: LabelAndClassName } = {
      [PaymentStatuses.AMP_INVOICE_REQUESTED]: {
        label: "AMP invoice requested",
        className: "amp-invoice-requested",
      },
      [PaymentStatuses.INVOICE_REQUEST_SENT]: showInvoiceRequestSent
        ? { label: "Invoice request sent", className: "hold-period" }
        : { label: "Hold period", className: "hold-period" },

      [PaymentStatuses.AMP_INVOICE_PAID]: {
        label: "AMP invoice paid",
        className: "amp-invoice-paid",
      },

      // This previously had the wrong label ("AMP invoice paid")
      [PaymentStatuses.PAYMENT_REQUEST_SENT]: {
        label: "Payment request sent",
        className: "payment-request-sent",
      },

      [PaymentStatuses.ARTIST_PAID]: {
        label: "Artist paid",
        className: "artist-paid",
      },
      [PaymentStatuses.ARTIST_PAYMENT_REQUESTED]: {
        label: "Artist payment requested",
        className: "artist-payment-requested",
      },
      [PaymentStatuses.CREDIT_NOTE_ISSUED]: {
        label: "Credit note issued",
        className: "credit-note-issued",
      },
      [PaymentStatuses.CREDIT_NOTE_REQUESTED]: {
        label: "Credit note requested",
        className: "credit-note-requested",
      },
      [PaymentStatuses.HOLD_PERIOD]: {
        label: "Hold period",
        className: "hold-period",
      },
      [PaymentStatuses.NOT_APPLICABLE]: {
        label: "Not applicable",
        className: "not-applicable",
      },
      [PaymentStatuses.PENDING]: {
        label: "Pending",
        className: "pending",
      },

      [PaymentStatuses.CLOSED]: {
        label: "Closed",
        className: "closed",
      },
      [PaymentStatuses.RETURNED]: {
        label: "Returned",
        className: "returned",
      },
    };

    return map[status as PaymentStatuses];
  }

  return undefined;
};

export const editablePaymentStatuses = [
  PaymentStatuses.HOLD_PERIOD,
  PaymentStatuses.NOT_APPLICABLE,
  PaymentStatuses.PENDING,
];

export const nonEligibleResaleStatuses = [
  ResaleStatuses.PROVISIONALLY_INELIGIBLE,
  ResaleStatuses.PROVISIONALLY_ELIGIBLE,
  ResaleStatuses.CONFIRMED_INELIGIBLE,
  ResaleStatuses.INVALID,
  ResaleStatuses.RESEARCH_REQUIRED,
];

export const canEditDetails = (
  payment_status?: PaymentStatuses,
  resale_status?: ResaleStatuses,
  isPastReport?: boolean,
) => {
  const payment_allowed = payment_status !== undefined && editablePaymentStatuses.includes(payment_status);
  const resale_allowed =
    resale_status !== undefined &&
    (nonEligibleResaleStatuses.includes(resale_status) || resale_status === ResaleStatuses.RESEARCH_REQUIRED);
  return (payment_allowed && !isPastReport) || (resale_allowed && isPastReport);
};

export const resaleStatusOptions: ResaleStatuses[] = [
  ResaleStatuses.CONFIRMED_ELIGIBLE,
  ResaleStatuses.CONFIRMED_INELIGIBLE,
  ResaleStatuses.INVALID,
  ResaleStatuses.RESEARCH_REQUIRED,
  ResaleStatuses.OPTED_OUT,
];
export const remediateResaleStatusOptions: ResaleStatuses[] = [
  ResaleStatuses.CONFIRMED_ELIGIBLE,
  ResaleStatuses.RESEARCH_REQUIRED,
];

export const researchRequiredReasonOptions: Partial<ResaleStatusReasons>[] = [
  ResaleStatusReasons.LIVING_ARTIST_NOT_YET_LOCATED,
  ResaleStatusReasons.LIVING_ARTIST_INDIGENOUS_NOT_MEMBER_OF_ART_CENTRE,
  ResaleStatusReasons.DECEASED_ARTIST,
  ResaleStatusReasons.BANK_DETAILS_REQUIRED,
  ResaleStatusReasons.DECEASED_INDIGENOUS_ARTIST,
  ResaleStatusReasons.OVERSEAS_ARTIST,
  ResaleStatusReasons.OTHER,
];

export const optedOutReasonOptions: Partial<ResaleStatusReasons>[] = [
  ResaleStatusReasons.CHARITY_EVENT,
  ResaleStatusReasons.COLLECT_THEMSELVES,
  ResaleStatusReasons.ARRANGEMENT_WITH_AGENT,
  ResaleStatusReasons.RIGHTSHOLDER_SALE,
  ResaleStatusReasons.NEGATIVE_IMPACT_ON_ARTISTS_SALES,
  ResaleStatusReasons.UNKNOWN,
  ResaleStatusReasons.OTHER,
];

// utils function for resale status dropdowns
export interface StatusAndReasonOption {
  status: ResaleStatuses;
  reason?: ResaleStatusReasons;
}

// Serialise helper function - react bootstrap eventKey does not support object
export const serialiseOption = (reasonAndOption: StatusAndReasonOption): string => JSON.stringify(reasonAndOption);
export const deSerialiseOption = (reasonAndOptionString: string): StatusAndReasonOption =>
  JSON.parse(reasonAndOptionString);
